import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { observer } from 'mobx-react'
import { autorun } from 'mobx'
import { autobind } from 'core-decorators'
import classNames from 'classnames'
import PerfectScrollbar from 'perfect-scrollbar'
import { formatMessage, FormattedMessage } from '../../translations'
import { contentLangs } from '../../config'

import { deepGet } from '../../shared/obj'
import Icon from '../../shared/icons'
import ContentLoadingBox from '../../shared/components/ContentLoadingBox'
import Dropdown, {
  DropdownContainer,
  Entry
} from '../../shared/components/Dropdown'
import GenevaDropdownOpener from '../../ui/components/GenevaDropdownOpener'

import { censhareStore } from '../reducer'

const css = /* typeof window === 'undefined' ? {} : */ require('../styles.scss')

// const possibleFormats = ['jpg-big', 'jpg-small']
const possibleFormats = ['jpg-big', 'jpg-small', 'png-small', 'png-big']


@observer
export default class CenshareSidebar extends Component {

  static propTypes = {
    pageStore: PropTypes.object.isRequired,
    templateStore: PropTypes.object.isRequired,
    articleStore: PropTypes.object.isRequired,
    projectStore: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)

    const pageModel = props.viewModels.models.data.page
    const templateModel = props.viewModels.models.data.template
    const censhareArticleIds = deepGet(pageModel, 'meta.censhareArticleIds') || []
    const isLocked = deepGet(templateModel, 'config.censhareLock') || false
    let displayState = 'info-state'

    this.ps = null

    if (isLocked) {
      displayState = 'info-state'
    }
    else if (censhareArticleIds.length === 1) {
      displayState = 'asset-state'
    }
    else if (censhareArticleIds.length > 1) {
      displayState = 'article-state'
    }

    const connectedElementIds = this.getConnectedElementIds()

    this.state = {
      isLoading: false,
      displayState,
      currentArticle: {},
      currentAsset: {},
      pageModel,  // to investigate: possible needed for re-renders
      templateModel,  // to investigate: possible needed for re-renders
      connectedElementIds,
      copiedFrom: null,
    }

    // Track only the article IDs - articles themselves will not be in state
    this.collections = {
      censhareArticleIds
    }

  }

  componentDidMount() {
    this.loadCenshareArticles()
    this.handler = autorun(() => this.updateConnectedElementIds(
      this.props.articleStore.current.phAccessor
      && this.props.articleStore.current.phAccessor.asJSON
    ))
    this.ps = new PerfectScrollbar('#censhareSidebar-container', {
      suppressScrollX: true
    })

  }

  componentWillUnmount() {
    if (this.handler) {
      this.handler()
    }
  }

  updateConnectedElementIds(data) {
    if (!data) {
      // Bail out if no data
      return
    }

    this.setState({
      connectedElementIds: this.getConnectedElementIds()
    })

    return
  }

  /** Pull connected placeholder information from the article model */
  getConnectedElementIds() {
    const phAccessor = deepGet(this, 'props.articleStore.current.phAccessor')
    const retVal = []

    Object.keys(phAccessor.placeholderMap).forEach((key) => {
      const source = phAccessor.getSource(key)
      if (source) {
        retVal.push({
          assetId: source.ctx.assetId,
          elementId: source.id,
          imageId: source.ctx.imageId,
          lastContentModification: source.ctx.lastContentModification,
          usedFormat: source.format
        })
      }
    })

    return retVal
  }

  loadCenshareArticles() {
    const { censhareArticleIds } = this.collections

    if (censhareArticleIds.length === 0) {
      return
    }

    this.setState({ isLoading: true })

    const contentLangKey = Object.keys(contentLangs).find(key => key === deepGet(this, 'props.articleStore.current.createdIso'))
    const params = {
      languageIso: contentLangs[contentLangKey].censhareIso
    }

    // Load all articles in parallel
    const loadPromises = censhareArticleIds.map(censhareArticleId => censhareStore.load(censhareArticleId, { params }))

    Promise.all(loadPromises)
      .then((models) => {
        // Filter out any null/undefined results
        const censhareArticles = models.filter(n => n)
        const connectedElementIds = this.state.connectedElementIds

        // Process each article's assets and elements - augment with format info
        censhareArticles.forEach((article) => {
          const articleAssets = article.content || []

          articleAssets.forEach((asset) => {
            const assetElements = asset.elements || []

            // Augment elements directly on the asset (maintaining reference)
            assetElements.forEach((element) => {
              if (element.type === 'image') {
                const imageObj = deepGet(element, 'value.image')
                if (imageObj) {
                  // Add available formats directly to the element object
                  element.availableFormats = possibleFormats.filter(format => imageObj[format])

                  const connectedElement = connectedElementIds.find(el => el.imageId === imageObj.id)

                  // Set selected format if not already set
                  if (!element.selectedFormat && element.availableFormats.length > 0) {
                    if (connectedElement && connectedElement.usedFormat) {
                      element.selectedFormat = connectedElement.usedFormat
                    }
                    else {
                      element.selectedFormat = element.availableFormats[0]
                    }
                  }
                }
              }
            })
          })
        })

        // Determine initial display state and current items
        let displayState = 'article-state'
        let currentArticle = {}
        let currentAsset = {}

        // Auto-navigate if only one article
        if (censhareArticles.length === 1) {
          currentArticle = censhareArticles[0]
          const assets = currentArticle.content || []

          if (assets.length === 1) {
            currentAsset = assets[0]
            const elements = currentAsset.elements || []
            displayState = elements.length > 0 ? 'element-state' : 'asset-state'
          }
          else {
            displayState = 'asset-state'
          }
        }

        // Update state once with all processed data
        this.setState({
          censhareArticles,
          currentArticle,
          currentAsset,
          displayState,
          isLoading: false
        })
      })
      .catch((err) => {
        console.error('Error loading censhare articles:', err)
        this.setState({ isLoading: false })
      })
  }

  @autobind
  handleDragStart(event, element) {
    const { currentArticle, currentAsset } = this.state
    const type = typeof element.value === 'string' ? 'text' : 'image'

    // Get either text or image url
    let value = typeof element.value === 'string'
      ? element.value
      : deepGet(element, 'value.image.downloadLink')

    let imageId = null
    let imageMetas = null
    let selectedFormat = ''

    if (element.type === 'image') {
      imageId = deepGet(element, 'value.image.id')
      imageMetas = {
        alt: deepGet(element, 'value.alt'),
        title: deepGet(element, 'value.title')
      }

      const imageObj = deepGet(element, 'value.image')
      if (!imageObj) {
        return
      }

      // Use the format stored on the element
      selectedFormat = element.selectedFormat

      if (selectedFormat) {
        value = deepGet(element, `value.image.${selectedFormat}.downloadLink`)
      }
    }

    const dataToSend = JSON.stringify({
      source: {
        id: element.id,
        name: 'censhare',
        format: selectedFormat,
        ctx: {
          articleId: currentArticle.assetId,
          assetId: currentAsset.assetId,
          imageId,
          lastContentModification: type === 'image'
            ? deepGet(element, 'value.image.lastContentModification')
            : currentAsset.lastContentModification
        }
      },
      value,
      imageMetas,
      type: element.type,
    })

    // If dragging image, set value to real image instead of thumbnail
    if (event.dataTransfer.types.includes('text/uri-list') || element.type === 'image') {
      event.dataTransfer.setData('text/uri-list', value)
    }

    // Extra logic for Firefox
    if (event.dataTransfer.types.includes('text/x-moz-url')) {
      event.dataTransfer.clearData('text/x-moz-url')
    }

    event.dataTransfer.effectAllowed = 'copy'
    event.dataTransfer.setData(`censhare-${type}`, dataToSend)
  }

  @autobind
  handleCenshareArticleClick(article) {
    this.setState({
      displayState: 'asset-state',
      currentArticle: article
    })
  }

  @autobind
  handleCenshareAssetClick(asset) {
    if (!asset.elements) {
      // Bail out if no content to display
      return
    }

    this.setState({
      displayState: 'element-state',
      currentAsset: asset
    })
  }

  @autobind
  handleBack(targetState) {
    this.setState({
      displayState: targetState
    })
  }


  @autobind
  handleDropdownChange({ target }) {
    // Find the element in the current asset and update it
    const { currentAsset } = this.state
    if (!currentAsset.elements) {
      return
    }

    const element = currentAsset.elements.find(el => el.id === target.id)
    if (element) {
      element.selectedFormat = target.identifier
      // Force re-render to update dropdown display
      this.forceUpdate()
    }
  }

  @autobind
  handleCopyClipboard(element) {
    let text = ''
    // copy either text or title + alt to clipboard
    if (element.type === 'image') {
      const temp = [element.value && element.value.title, element.value && element.value.alt].filter(n => n)
      if (temp.length > 1) {
        text = temp.join(' ')
      }
      else if (temp.length === 1) {
        text = temp[0]
      }
    }
    else {
      text = element.value
    }

    navigator.clipboard.writeText(text)
      .then(() => {
        this.setState({
          copiedFrom: element.id
        })
        // wait 3 seconds and reset
        setTimeout(() => {
          this.setState({
            copiedFrom: null
          })
        }, 4000)
      }).catch((err) => {
        console.error('Could not copy text: ', err)
      })
  }

  @autobind
  checkConnectedElementsInArticle(article) {
    const { connectedElementIds } = this.state
    let connected = false
    if (!article.content) {
      return connected
    }
    article.content.forEach((asset) => {
      asset.elements.forEach((element) => {
        if (connectedElementIds.find(el => el.elementId === element.id)) {
          connected = true
        }
      })
    })
    return connected
  }

  // Render the dropdown for image format selection
  @autobind
  renderDropdown(element, isElementConnected) {
    if (element.type !== 'image') {
      // Bail out - only images get a dropdown
      return null
    }
    if (!element.value.image) {
      // Bail out if only image placeholder
      return null
    }

    // Use formats stored directly on the element
    const availableFormats = element.availableFormats || []
    const imageFormat = element.selectedFormat

    if (availableFormats.length === 0) {
      return null
    }

    // If only one option and not connected, show as text with special styling
    if (availableFormats.length === 1 && !isElementConnected) {
      return (
        <span className="image-format-single">
          <FormattedMessage id={`censhare.sidebar.imagetype.${imageFormat}`} />
        </span>
      )
    }

    // Multiple options: show dropdown
    return (<DropdownContainer className="image-format-dropdown">
      <GenevaDropdownOpener
        clickToClose
        caret={false}
        arrow
        disabled={isElementConnected}
      >
        <FormattedMessage id={`censhare.sidebar.imagetype.${imageFormat}`} />
      </GenevaDropdownOpener>

      <Dropdown>
        {availableFormats.map((option) => {
          return (<Entry
            id={element.id}
            identifier={option}
            key={option}
            onClick={event => this.handleDropdownChange(event)}
          >
            <FormattedMessage id={`censhare.sidebar.imagetype.${option}`} />
          </Entry>)
        })}
      </Dropdown>
    </DropdownContainer>)
  }

  renderLoader() {
    return (<div className="content-loader-background">
      <ContentLoadingBox className="content-loader"
        message={{
          id: 'censhare.loading',
        }} />
    </div>)
  }

  renderConnectionInfo() {
    const { templateModel } = this.state

    return (<div className="censhare-connection-info">
      {templateModel && templateModel.config && templateModel.config.censhareLock
        ? <FormattedMessage id="censhare.sidebar.template-lock" />
        : [<FormattedMessage key="info-1" id="censhare.sidebar.connection-info-1" />,
          <FormattedMessage key="info-2" id="censhare.sidebar.connection-info-2" />]}
    </div>)
  }

  renderArticleOverview() {
    const { censhareArticles } = this.state
    if (!censhareArticles) {
      return null
    }

    return (<div className="censhare-article-overview">
      <div className="article-info">
        <FormattedMessage id="censhare.sidebar.article.info" />
      </div>
      {censhareArticles.map((article) => {
        const hasConnectedElement = this.checkConnectedElementsInArticle(article)
        return (<div className="censhare-article-item" key={article.assetId} onClick={() => this.handleCenshareArticleClick(article)}>
          <div className="censhare-article-item-content">
            <div className="censhare-article-item-container">
              <div>{article.name}</div>

              {article.createdAt
                ? <span>{article.createdAt} | </span>
                : null}

              {article.category
                ? <span>{article.category} | </span>
                : null}
              {hasConnectedElement
                ? <div className="article-item-icon-holder"><Icon name="ion ion-ios-checkmark" /></div>
                : null}
            </div>

            <span>ID {article.assetId}</span>
          </div>
        </div>)
      })}
    </div>)
  }

  renderAssetOverview() {
    const { currentArticle, connectedElementIds } = this.state
    const assets = currentArticle.content || []
    const assetText = formatMessage({ id: 'censhare.assets' }, { count: assets.length })

    return (<div className="censhare-asset-overview">
      <div
        onClick={() => this.handleBack('article-state')}
        className="back-button"
      >
        <Icon name="ion ion-ios-arrow-down" />
        <FormattedMessage id="article.article" />
      </div>
      <div className="article-name">
        {currentArticle.name}
      </div>
      <div className="asset-info">
        {assetText}
      </div>
      {assets.map((asset) => {
        const elements = asset.elements || []
        let isElementConnected = false
        elements.forEach((element) => {
          if (!isElementConnected) {
            isElementConnected = !!connectedElementIds.find(el => el.elementId === element.id)
          }
        })

        return (<div key={asset.assetId} className="asset-item" onClick={() => this.handleCenshareAssetClick(asset)}>
          <span>{asset.name}</span>
          <div className="asset-item-icon-holder">
            {isElementConnected
              ? <Icon name="ion ion-ios-checkmark" />
              : null}
          </div>
        </div>)
      })}
    </div>)
  }

  renderElementOverview() {
    const { currentAsset, connectedElementIds, copiedFrom } = this.state
    const elements = currentAsset.elements || []

    return (<div className="censhare-content-overview">
      <div
        onClick={() => this.handleBack('asset-state')}
        className="back-button"
      >
        <Icon name="ion ion-ios-arrow-down" />
        <FormattedMessage id="censhare.sidebar.asset" />
        <div className="asset-id">
          <FormattedMessage id="censhare.sidebar.id" />
          <span>{currentAsset.assetId}</span>
        </div>
      </div>
      <div className="asset-name">
        {currentAsset.name}
      </div>
      {elements.map((element) => {
        const typeText = formatMessage({ id: `censhare.sidebar.contenttype.${element.type}` })
        const isElementConnected = !!connectedElementIds.find(el => el.elementId === element.id)

        if (!element.value) {
          // Bail out if element has no value to show
          return null
        }

        let showCopyIcon = true
        if (element.type === 'image') {
          // For images, only show copy icon if title or alt text is present
          if (!element.value || (!element.value.title || element.value.title.trim() === '') && (!element.value.alt || element.value.alt.trim() === '')) {
            showCopyIcon = false
          }
        }

        return (<div
          key={element.id}
          className="element-item-wrapper"
        >
          <div className="element-item-type">
            <span>{typeText}</span>

            {this.renderDropdown(element, isElementConnected)}

            {showCopyIcon
              ? <div onClick={() => this.handleCopyClipboard(element)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 448 512" style={{ fill: copiedFrom === element.id ? '#327ccb' : '#000' }}>
                  <path d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z" />
                </svg>
              </div>
              : null}

            {isElementConnected
              ? <Icon name="ion ion-ios-checkmark" />
              : null}
          </div>
          {this.renderElement(element, isElementConnected)}
        </div>)
      })}
    </div>)
  }

  renderElement(element, isConnected) {
    // image content
    if (typeof element.value === 'object') {


      const thumbnailURL = deepGet(element, 'value.image.thumbnail.downloadLink')
      const lastContentModification = deepGet(element, 'value.image.lastContentModification')
      const connectedElement = this.state.connectedElementIds.find(el => el.elementId === element.id)
      let isNew = false

      if (connectedElement && new Date(connectedElement.lastContentModification) < new Date(lastContentModification)) {
        isNew = true
      }

      return (<div
        className={classNames('element-item', 'element-item-image', isConnected && !isNew ? 'connected' : '')}
        draggable
        onDragStart={event => this.handleDragStart(event, element)}
      >
        <div className="image-wrapper">
          { element.value.image
            ? <img
              src={thumbnailURL}
              alt={element.value.alt}
              title={element.value.title}
            />
            : <div className="empty-image">
              <FormattedMessage id="censhare.sidebar.missing-image" />
            </div>
          }
          <FormattedMessage className="alt-seo-title" id="censhare.sidebar.image.seo" />
          <div>{element.value.title || '-'}</div>
          <FormattedMessage className="alt-seo-title" id="censhare.sidebar.image.alt" />
          <div>{element.value.alt || '-'}</div>
        </div>
        {isNew
          ? <div className="new-banner">
            <FormattedMessage id="image.image-propagation-new" />
          </div>
          : null}
      </div>)
    }

    // text content
    if (typeof element.value === 'string') {

      return (<div
        className={classNames('element-item', isConnected ? 'connected' : '')}
        draggable={!isConnected}
        onDragStart={event => this.handleDragStart(event, element)}
        dangerouslySetInnerHTML={{
          __html: element.value
        }}
      />)
    }

    return null
  }

  renderContent() {
    const { displayState } = this.state

    if (displayState === 'info-state') {
      return this.renderConnectionInfo()
    }

    if (displayState === 'article-state') {
      return this.renderArticleOverview()
    }

    if (displayState === 'asset-state') {
      return this.renderAssetOverview()
    }

    if (displayState === 'element-state') {
      return this.renderElementOverview()
    }

    return this.renderLoader()
  }

  render() {

    if (this.ps) {
      this.ps.update()
    }

    return (
      <div
        id="censhareSidebar-container"
        className={classNames('grid-block vertical', css.censhareTab)}
        onFocus={() => this.handleMouseOver()}
      >
        {this.state.isLoading
          ? this.renderLoader()
          : this.renderContent()}
      </div>
    )
  }
}
